import React, { useContext, useEffect, useState } from "react";
import {
  Layout,
  Image,
  Select,
  Space,
  Segmented,
  Button,
  notification,
  Dropdown,
} from "antd";
import UserSettings from "../CustomLayout/UserSettings";
import { UserContext } from "../../../context/UserContext";
import { getCookie, setCookie } from "../../../helpers/localStorage";
import { getRoleLabelWithFacility } from "../../../helpers/getRoleLabel";
import { useLocation, useNavigate } from "react-router-dom";
import { BellOutlined, MenuOutlined } from "@ant-design/icons";
import { Badge } from "antd";
import { useChatNotifications } from "../../../hooks/useChatNotifications";
import DocSideMenu from "./DocSidemenu";
import NotificationPopover from "./NotificationPopover";
import ChatToast from "./ChatToast";
import { FacilityID, IS_HOD, Role } from "../../../constants/defaultKeys";
import Search from "../CustomLayout/Search";
import { PatientTypeContext } from "./PatientTypeContextProvider";
import { redirectToDashboard } from "../../../helpers/auth/helper";
import useIsMobile from "../../../hooks/useIsMobile";
import "./DoctorLayout.css";

const { Option } = Select;
const { Header, Footer, Content } = Layout;

const DoctorLayout = ({
  children,
  footer,
  headerExtra = true,
  showPatientSegment = false,
  header,
  showNotifications = false,
  lockPageScroll = false,
}) => {
  const location = useLocation();
  const url = new URL(window?.location?.href);
  const isHome = location?.pathname?.endsWith("/home");
  const navigate = useNavigate();
  const { userDetails, setUserDetails } = useContext(UserContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(
    Number(getCookie(FacilityID)) ||
      userDetails?.activeOrg?.roles?.[0]?.facility_id
  );
  const [selectedRole, setSelectedRole] = useState(
    getCookie(Role) || userDetails?.activeOrg?.roles?.[0]?.role
  );
  const defaultRole = userDetails?.currRole || getCookie(Role);
  console.log(defaultRole);
  const role = defaultRole?.toLowerCase().replaceAll("_", "-");
  const isReceptionist = defaultRole?.toLowerCase() === "receptionist";
  const isNurse = defaultRole === "Nurse";
  const isMobile = useIsMobile();
  const {
    notifications,
    notification: notif,
    unreadBadgeCount,
    refetch: refetchNotifications,
    handleNotificationClick,
  } = useChatNotifications();
  const [visible, setVisible] = useState(false);

  const handleChatNavigation = (patientId, chat_type) => {
    const newUrl = new URL(window.location.href);
    newUrl.pathname = `/${role}/home`;
    newUrl.searchParams.set("patient", patientId);
    newUrl.searchParams.set("type", "chat");
    newUrl.searchParams.set("chat", chat_type);

    navigate(`${newUrl.pathname}${newUrl.search}`, { replace: true });
  };

  const onNotificationItemClick = (item) => {
    handleChatNavigation(item?.patient_id, item?.chat_type);
    setVisible(false);
    handleNotificationClick(item);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isOnHome = location.pathname.endsWith("/home");
    const isOnPatientChat =
      searchParams.get("type") === "chat" && searchParams.get("patient");

    if (isOnHome && !isOnPatientChat) {
      refetchNotifications();
    }
  }, [location.pathname, location.search, refetchNotifications]);
  useEffect(() => {
    if (notif && notif?.notification !== "user_left") {
      notification.open({
        className: "chat-toast",
        icon: null,
        message: <ChatToast extra={notif?.extra} />,
        duration: 5,
        placement: "topRight",
        onClick: () => {
          const chatType =
            notif?.extra?.chat_type || notif?.extra?.session_id?.split("_")[1];
          handleChatNavigation(notif?.extra?.patient_id, chatType);
          handleNotificationClick({
            patient_id: notif?.extra?.patient_id,
            ip_id:
              notif?.extra?.ip_id ||
              notif?.extra?.session_id?.split?.("_")?.[0],
            session_id: notif?.extra?.session_id,
            chat_type: chatType,
          });
          notification.destroy();
        },
        showProgress: true,
        pauseOnHover: true,
      });
    }
  }, [notif]);

  const handleRoleChange = (value) => {
    const lastUnderscoreIndex = value.lastIndexOf("_");

    // Extract role by slicing up to the last underscore
    const role = value.slice(0, lastUnderscoreIndex);

    // Extract facility ID by slicing after the last underscore
    const facility_id = value.slice(lastUnderscoreIndex + 1);

    const is_hod = userDetails?.activeOrg?.roles?.find(
      (record) =>
        record?.role === role && record?.facility_id === Number(facility_id)
    )?.is_hod;
    const roleObj = userDetails?.activeOrg?.roles?.find(
      (record) =>
        record.role === role && record.facility_id === Number(facility_id)
    );

    redirectToDashboard(userDetails?.activeOrg?.domain, role, roleObj);

    setSelectedRole(value);
    setSelectedFacility(facility_id);
    setCookie(Role, role, `.${process.env.REACT_APP_PUBLIC_DOMAIN}`, "/");
    setCookie(IS_HOD, is_hod, `.${process.env.REACT_APP_PUBLIC_DOMAIN}`, "/");
    setCookie(
      FacilityID,
      facility_id,
      `.${process.env.REACT_APP_PUBLIC_DOMAIN}`,
      "/"
    );
    setUserDetails((prev) => ({
      ...prev,
      currRole: role,
      currFacility: facility_id,
    }));
  };

  const { patientType, setPatientType } = useContext(PatientTypeContext);
  const patientTypeOptions = [
    { label: "In Patients", value: "inPatients", key: "ip" },
    { label: "Out Patients", value: "outPatients", key: "op" },
  ];
  const activeRoleObj = userDetails?.activeOrg?.roles?.find(
    (r) => r.role === selectedRole && r.facility_id === Number(selectedFacility)
  );

  const filteredOptions = patientTypeOptions?.filter((opt) => {
    if (opt.key === "ip") return activeRoleObj?.ip_available;
    if (opt.key === "op") return activeRoleObj?.op_available;
    return false;
  });

  const onPatientTypeChange = (value) => {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("patient-type", value);
    nextUrl.searchParams.set(
      "status",
      value === "inPatients" ? "current" : "today"
    );
    // Don't carry OP/IP list filters across patient-type switches
    nextUrl.searchParams.delete("search");
    nextUrl.searchParams.delete("page");
    nextUrl.searchParams.delete("floor");
    nextUrl.searchParams.delete("assessment_date");
    navigate(`${nextUrl.pathname}${nextUrl.search}`, {
      replace: true,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(document?.location?.search);
    const status = params?.get("patient-type");
    if (defaultRole !== "Nurse") {
      setPatientType(status);
    }
  }, [url]);

  const overlay = (
    <NotificationPopover
      notifications={notifications}
      unreadCount={unreadBadgeCount}
      onItemClick={onNotificationItemClick}
    />
  );

  return (
    <Layout
      className={[
        "layoutContent",
        lockPageScroll ? "doctor-patient-list-lock" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ minHeight: "100vh" }}
    >
      {header === true && (
        <Header className="headerStyle headerStylesWithoutSide">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "64px",
              padding: "0 16px",
            }}
          >
            <div className="mobile-only" style={{ flex: 1, textAlign: "left" }}>
              <Space>
                <MenuOutlined
                  style={{ fontSize: 18 }}
                  onClick={() => setMenuOpen(true)}
                />
                <Image
                  preview={false}
                  src={userDetails?.activeOrg?.org_logo}
                  style={{ width: 28 }}
                />
              </Space>
            </div>

            <div
              className="mobile-only mobile-patient-dropdown"
              style={{ flex: 2 }}
            >
              {showPatientSegment && isHome && (
                <Select
                  value={patientType}
                  onChange={onPatientTypeChange}
                  bordered={false}
                  size="small"
                  className="patient-type-select"
                >
                  {filteredOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              )}
            </div>

            <div
              className="mobile-only"
              style={{ flex: 1, textAlign: "right" }}
            >
              {!isReceptionist && (
                <Dropdown
                  dropdownRender={() => overlay}
                  trigger={["click"]}
                  placement="bottomRight"
                  overlayClassName="notif-dropdown"
                  getPopupContainer={() => document.body}
                >
                  <Badge count={unreadBadgeCount} size="small" overflowCount={99}>
                    <Button
                      type="text"
                      icon={<BellOutlined style={{ fontSize: 18 }} />}
                    />
                  </Badge>
                </Dropdown>
              )}
            </div>

            <div
              className="desktop-only flex-between"
              style={{ width: "100%" }}
            >
              <div className="tabWebDisplay">
                <Space>
                  {!isNurse && (
                    <MenuOutlined
                      style={{ fontSize: 18 }}
                      onClick={() => setMenuOpen(true)}
                    />
                  )}
                  <Image
                    preview={false}
                    src={userDetails?.activeOrg?.org_logo}
                    style={{ width: 30 }}
                  />
                  {showPatientSegment && isHome && (
                    <Segmented
                      options={filteredOptions}
                      value={patientType}
                      onChange={onPatientTypeChange}
                    />
                  )}
                </Space>
              </div>

              {headerExtra && (
                <div className="flex-end" style={{ gap: 24 }}>
                  {userDetails?.activeOrg?.roles?.length > 1 && (
                    <Select
                      value={`${selectedRole}_${selectedFacility}`}
                      onChange={handleRoleChange}
                      style={{ width: 350 }}
                    >
                      {userDetails?.activeOrg?.roles?.map(
                        (role) =>
                          role.role !== "Partner" && (
                            <Option
                              key={`${role.role}_${role.facility_id}`}
                              value={`${role.role}_${role.facility_id}`}
                            >
                              {getRoleLabelWithFacility(role)}
                            </Option>
                          )
                      )}
                    </Select>
                  )}

                  {!isReceptionist && (
                    <Dropdown
                      dropdownRender={() => overlay}
                      trigger={["click"]}
                      placement="bottomRight"
                      overlayClassName="notif-dropdown"
                      getPopupContainer={() => document.body}
                      arrow={false}
                    >
                      <Badge count={unreadBadgeCount} size="small" overflowCount={99}>
                        <Button
                          type="text"
                          className="notif-bell-btn"
                          icon={<BellOutlined style={{ fontSize: 18 }} />}
                        />
                      </Badge>
                    </Dropdown>
                  )}

                  <UserSettings />
                </div>
              )}
            </div>
          </div>
        </Header>
      )}

      {typeof header === "object" && header && (
        <div className="doctor-inflow-header">{header}</div>
      )}

      <Content
        className={[
          header === true
            ? "contentStyle"
            : typeof header === "object" && header
              ? "contentWithInFlowHeader"
              : "contentNoHeaderStyle",
          footer ? "doctor-layout-content-with-footer" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          paddingBottom: footer
            ? isMobile
              ? "64px"
              : "72px"
            : lockPageScroll
              ? "8px"
              : "32px",
        }}
      >
        {children}
      </Content>
      {footer && (
        <Footer
          className={`footerStyle footerStyleWithOutSide doctor-layout-footer`}
        >
          {footer}
        </Footer>
      )}

      {menuOpen && (
        <DocSideMenu
          open={menuOpen}
          setOpen={setMenuOpen}
          activeRoleObj={activeRoleObj}
          onRoleChange={handleRoleChange}
          hideNavMenu={isNurse}
        />
      )}
    </Layout>
  );
};

export default DoctorLayout;
